import { RouteProp, useNavigation, useRoute } from '../../../navigation/native';
import { Formik, FormikProps } from 'formik';
import React, { useRef } from 'react';
import { useDispatch } from 'react-redux';
import * as yup from 'yup';

import { RootStackParamList } from '../../../app/Navigation';
import { Route } from '../../../app/Route';
import { getConfigurationsForAllRows } from '../../../helpers/Getters';
import {
  Snippet,
  SnippetConfigurationColumn,
  StandingOrderStatus,
} from '../../../services/dataModels';
import {
  editingActions,
  selectEditedSnippets,
  selectEditingMode,
  selectHasEditSomeSnippet,
} from '../../../store';
import { AppDispatch } from '../../../store/store';
import Layout from '../Layout';
import SaveButton from '../SaveButton';
import Header from './Header';
import RowForm from './RowForm';

const getValidationSnippet = (snippet: Snippet): Snippet => {
  if (snippet.type === 'snippetGroup') {
    return {
      ...snippet,
      groupSnippets: snippet.groupSnippets.map((g) => getValidationSnippet(g)),
    };
  }

  return snippet;
};

const RowScreen = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigation = useNavigation();
  const { params } = useRoute<RouteProp<RootStackParamList, 'EditingRowScreen'>>();
  const formikRef = useRef<FormikProps<Snippet>>(null);

  const readOnly = selectEditingMode.useHook() === 'readOnly';
  const hasEditSomeSnippet = selectHasEditSomeSnippet.useHook();
  const snippet = selectEditedSnippets.useHook();

  const data = snippet.data[params.rollupIndex ?? 0];

  const canPostComment = () => {
    if (snippet.type === 'StandingOrder') {
      const status = data.rowData[params.rowIndex].status as StandingOrderStatus;
      return status !== 'Activated';
    }

    if (snippet.type === 'Task') return true;

    return false;
  };

  const configurationToValidation = (configuration: SnippetConfigurationColumn) => {
    if (configuration?.controlType === 'Numeric') {
      return {
        [configuration.name]: yup.number().nullable().typeError('Invalid value'),
      };
    }
    return {};
  };

  const createValidation = (snippet: Snippet) => {
    if (!snippet.configuration) {
      if (snippet.groupSnippets) {
        const g = snippet.groupSnippets.map((s) => createValidation(s));

        return yup.object({
          groupSnippets: yup
            .array()
            .of(
              yup.lazy((v) => g[snippet.groupSnippets.findIndex((a) => a.name === v.name)]) as any,
            ),
        });
      }
      return yup.object();
    }

    const rowConfiguration = getConfigurationsForAllRows(snippet);

    return yup.object({
      data: yup.array().of(
        yup.object({
          rowData: yup.array().of(
            yup.object(
              rowConfiguration.reduce(
                (previous, current) => ({
                  ...previous,
                  ...configurationToValidation(current),
                }),
                {},
              ),
            ),
          ),
        }),
      ),
    });
  };

  const validationSnippet = getValidationSnippet(
    params.rowIndex === data.rowData.length
      ? {
          ...snippet,
          data: snippet.data.map((d, i) =>
            i === (params.rollupIndex ?? 0)
              ? {
                  ...d,
                  rowCount: d.rowCount + 1,
                  rowData: d.rowData.concat(
                    snippet.configuration.columns.reduce(
                      (acc, cur) => ({ ...acc, [cur.name]: '' }),
                      {},
                    ),
                  ),
                }
              : d,
          ),
        }
      : snippet,
  );
  const validationSchema = createValidation(validationSnippet);

  const handlePostComment = () => {
    navigation.navigate(Route.postComment, { ...params });
  };

  const handleStatusPressed = () => {
    navigation.navigate(Route.actionListScreen, { ...params });
  };

  const handlePercentagePressed = () => {
    navigation.navigate(Route.updatePercentage, { ...params });
  };

  return (
    <Layout
      title={params.lastLink}
      action={
        hasEditSomeSnippet && (
          <SaveButton
            snippet={() => formikRef.current.values}
            onSuccess={() => formikRef.current.resetForm({ values: formikRef.current.values })}
          />
        )
      }
      onBackPressed={() => {
        formikRef.current.resetForm();
      }}
      onPostComment={canPostComment() && handlePostComment}
      header={
        <Header
          snippetType={snippet.type}
          row={data.rowData[params.rowIndex]}
          onStatusPressed={handleStatusPressed}
          onPercentagePressed={handlePercentagePressed}
          readOnly={readOnly}
        />
      }
      content={
        <Formik
          innerRef={formikRef}
          initialValues={snippet}
          enableReinitialize
          onSubmit={() => {}}
          validationSchema={validationSchema}>
          {({ values, dirty, isValid }) => {
            dispatch(editingActions.formStateChanged({ dirty, valid: isValid }));
            return (
              <RowForm
                values={values}
                rollupIndex={params.rollupIndex ?? 0}
                rowIndex={params.rowIndex ?? 0}
              />
            );
          }}
        </Formik>
      }
    />
  );
};

export default RowScreen;
